import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core';
import { FormattedMessage } from 'react-intl';
import { useFormikContext } from 'formik';
import { UserAvatar } from '~/modules/common/components/Avatars';
import RoleAssignmentDialog from '~/modules/tasks/components/RoleAssignmentDialog';
import { usePutTaskResourceEstimate } from '../../../hooks/usePutTaskResourceEstimate';
import useUpdateTaskResourceRoleAssignments from '../hooks/useUpdateTaskResourceRoleAssignments';

const useStyles = makeStyles(theme => ({
  title: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    ...theme.typography.h6
  }
}));

const EditResourceRoleAssignmentDialog = ({
  task,
  resourceEstimate,
  rowIndex,
  onClose
}) => {
  const classes = useStyles();
  const {
    updateTaskResourceRoleAssignments,
    loading: isSavingRoleAssignments
  } = useUpdateTaskResourceRoleAssignments();
  const {
    putTaskResourceEstimate,
    loading: isSavingResourceEstimate
  } = usePutTaskResourceEstimate();
  const { setFieldValue } = useFormikContext();

  const handleSave = useCallback(
    async ({
      roleAssignmentMode: mode,
      rolesToAssign,
      roleUrisToUnassign,
      defaultRole,
      totalRoleCount
    }) => {
      await updateTaskResourceRoleAssignments({
        taskUri: task?.id,
        resourceUserUri: resourceEstimate?.resourceUser?.uri,
        roleAssignmentMode: mode,
        rolesToAssign,
        roleUrisToUnassign
      });

      await putTaskResourceEstimate({
        taskId: task?.id,
        taskResourceEstimateId: resourceEstimate?.taskResourceEstimateId,
        projectRoleId: defaultRole?.id ?? null
      });

      setFieldValue(
        `resourceEstimates[${rowIndex}].taskResourceUserRoleAssignmentSummary`,
        {
          roleAssignmentMode: mode,
          defaultRole: defaultRole ?? null,
          totalRoleCount
        }
      );
      setFieldValue(
        `resourceEstimates[${rowIndex}].projectRole`,
        defaultRole ?? null
      );
    },
    [
      updateTaskResourceRoleAssignments,
      task?.id,
      resourceEstimate?.resourceUser?.uri,
      resourceEstimate?.taskResourceEstimateId,
      putTaskResourceEstimate,
      setFieldValue,
      rowIndex
    ]
  );

  const title = useMemo(
    () => (
      <span className={classes.title}>
        <FormattedMessage id="taskAssignmentRolesDialog.title" />
        <UserAvatar user={resourceEstimate?.resourceUser} size="small" />
        {resourceEstimate?.resourceUser?.displayText}
      </span>
    ),
    [classes.title, resourceEstimate?.resourceUser]
  );

  return (
    <RoleAssignmentDialog
      open
      onClose={onClose}
      onSave={handleSave}
      taskUri={task?.id}
      variant="taskResourceRole"
      teamMemberUserId={resourceEstimate?.resourceUser?.id}
      initialMode={
        resourceEstimate?.taskResourceUserRoleAssignmentSummary
          ?.roleAssignmentMode
      }
      savedDefaultRole={
        resourceEstimate?.taskResourceUserRoleAssignmentSummary?.defaultRole ??
        null
      }
      savedRoleCount={
        resourceEstimate?.taskResourceUserRoleAssignmentSummary
          ?.totalRoleCount ?? 0
      }
      title={title}
      isSaving={isSavingRoleAssignments || isSavingResourceEstimate}
    />
  );
};

EditResourceRoleAssignmentDialog.propTypes = {
  task: PropTypes.object,
  resourceEstimate: PropTypes.object.isRequired,
  rowIndex: PropTypes.number,
  onClose: PropTypes.func.isRequired
};

export default EditResourceRoleAssignmentDialog;
